import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { fetchMe, type Me } from '@/core/api/auth'

type AuthValue = { session: Me | null; loading: boolean; refresh: () => Promise<void> }

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  const startedRef = useRef(0)
  const appliedRef = useRef(0)
  const mountedRef = useRef(false)

  const request = useCallback(async (): Promise<void> => {
    const id = ++startedRef.current
    let me: Me | null
    try {
      me = await fetchMe()
    } catch (err) {
      if (mountedRef.current && id === startedRef.current) setLoading(false)
      throw err
    }
    if (mountedRef.current && id > appliedRef.current) {
      appliedRef.current = id
      setSession(me)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const load = () => {
      request().catch(() => undefined)
    }

    load()
    window.addEventListener('focus', load)
    return () => {
      mountedRef.current = false
      window.removeEventListener('focus', load)
    }
  }, [request])

  return (
    <AuthContext.Provider value={{ session, loading, refresh: request }}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthValue {
  const auth = useContext(AuthContext)
  if (!auth) throw new Error("useAuth must be used within AuthProvider")
  return auth
}
